import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import MeccaFlex from './pages/Main/KinetiCore';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminLeads from './pages/Admin/AdminLeads';
import AdminOrders from './pages/Admin/AdminOrders';
import AdminQuotes from './pages/Admin/AdminQuotes';
import AdminContent from './pages/Admin/AdminContent';
import AdminProducts from './pages/Admin/AdminProducts';
import AdminPricing from './pages/Admin/AdminPricing';
import AdminShipping from './pages/Admin/AdminShipping';
import AdminAnalytics from './pages/Admin/AdminAnalytics';
import AdminAutomation from './pages/Admin/AdminAutomation';
import AdminAgents from './pages/Admin/AdminAgents';
import AdminAgenticConsole from './pages/Admin/AdminAgenticConsole';
import AdminSettings from './pages/Admin/AdminSettings';
import AdminCustomers from './pages/Admin/AdminCustomers';
import AdminCustomerDetail from './pages/Admin/AdminCustomerDetail';
import AdminPipeline from './pages/Admin/AdminPipeline';
import AdminTasks from './pages/Admin/AdminTasks';
import AdminEmailTemplates from './pages/Admin/AdminEmailTemplates';
import AdminAuditLog from './pages/Admin/AdminAuditLog';

import AdminCompliance from './pages/Admin/AdminCompliance';
import AdminScreens from './pages/Admin/AdminScreens';
import AdminPartners from './pages/Admin/AdminPartners';
import AdminPartFiles from './pages/Admin/AdminPartFiles';
import AdminEngagements from './pages/Admin/AdminEngagements';
import AdminEngagementDetail from './pages/Admin/AdminEngagementDetail';
import PortalLogin from './pages/Portal/PortalLogin';
import PortalDashboard from './pages/Portal/PortalDashboard';
import QuoteSpecIntake from './pages/Main/QuoteSpecIntake';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#fff' }}>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MeccaFlex />} />
            <Route path="/quote/:leadId" element={<QuoteSpecIntake />} />
            <Route path="/admin" element={<Navigate to="/admin/login" />} />
            <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ProtectedRoute>
                <AdminLeads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute>
                <AdminOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quotes"
            element={
              <ProtectedRoute>
                <AdminQuotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <ProtectedRoute>
                <AdminContent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute>
                <AdminProducts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pricing"
            element={
              <ProtectedRoute>
                <AdminPricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/shipping"
            element={
              <ProtectedRoute>
                <AdminShipping />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/automation"
            element={
              <ProtectedRoute>
                <AdminAutomation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <ProtectedRoute>
                <AdminAgents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agentic"
            element={
              <ProtectedRoute>
                <AdminAgenticConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* CRM Routes */}
          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute>
                <AdminCustomers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/customers/:id"
            element={
              <ProtectedRoute>
                <AdminCustomerDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pipeline"
            element={
              <ProtectedRoute>
                <AdminPipeline />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tasks"
            element={
              <ProtectedRoute>
                <AdminTasks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <ProtectedRoute>
                <AdminEmailTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute>
                <AdminAuditLog />
              </ProtectedRoute>
            }
          />


          <Route
            path="/admin/compliance"
            element={
              <ProtectedRoute>
                <AdminCompliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/screens"
            element={
              <ProtectedRoute>
                <AdminScreens />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partners"
            element={
              <ProtectedRoute>
                <AdminPartners />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/part-files"
            element={
              <ProtectedRoute>
                <AdminPartFiles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/engagements"
            element={
              <ProtectedRoute>
                <AdminEngagements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/engagements/:id"
            element={
              <ProtectedRoute>
                <AdminEngagementDetail />
              </ProtectedRoute>
            }
          />

          {/* Customer Portal (passwordless magic link) */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal" element={<Navigate to="/portal/login" />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
        </ToastProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
